import { AlertTriangle, Info, Megaphone, Wrench, X } from "lucide-react";
import { useState } from "react";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/cn";
import type { TenantOverview } from "./queries";

const DISMISSED = "zeeta.dismissedAnnouncements";

function readDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED) ?? "[]") as string[];
  } catch {
    return [];
  }
}

/** Maintenance notice and Zeetabyte announcements, shown above every page of a business. */
export function PlatformBanners({ overview }: { overview: TenantOverview }) {
  const { t, locale } = useI18n();
  const [dismissed, setDismissed] = useState(readDismissed);
  const m = overview.maintenance;
  const list = (overview.announcements ?? []).filter(
    (a) => !a.dismissible || !dismissed.includes(a.id),
  );
  const dismiss = (id: string) => {
    const next = [...dismissed, id].slice(-50);
    setDismissed(next);
    try {
      localStorage.setItem(DISMISSED, JSON.stringify(next));
    } catch {
      // Private mode: the banner comes back next visit.
    }
  };
  if (!m && list.length === 0) return null;
  return (
    <div className="space-y-px">
      {m && (
        <div
          className="flex items-start gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-black"
          role="status"
        >
          <Wrench className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{m.message?.[locale] || t("platform.maintenance")}</span>
        </div>
      )}
      {list.map((a) => {
        const Icon =
          a.severity === "critical" ? AlertTriangle : a.severity === "warning" ? Megaphone : Info;
        return (
          <div
            key={a.id}
            className={cn(
              "flex items-start gap-2 px-4 py-2 text-sm",
              a.severity === "critical"
                ? "bg-destructive text-white"
                : a.severity === "warning"
                  ? "bg-amber-100 text-amber-950 dark:bg-amber-950 dark:text-amber-100"
                  : "bg-accent text-accent-foreground",
            )}
            role="status"
          >
            <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="flex-1">
              <span className="font-semibold">{a.title[locale]}</span>
              {a.body[locale] && <span className="block">{a.body[locale]}</span>}
            </span>
            {a.dismissible && (
              <button
                type="button"
                aria-label={t("platform.dismiss")}
                onClick={() => dismiss(a.id)}
              >
                <X className="size-4" aria-hidden />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
